import {
  gameSlots,
  poolsOf,
  resolveSlot,
  type Division,
  type Pool,
  type ScheduledGame,
  type Team,
} from "./types";

export interface StandingRow {
  team: Team;
  played: number;
  wins: number;
  losses: number;
  draws: number;
  pointsFor: number;
  pointsAgainst: number;
  /** Point differential: the ultimate tiebreak in pool play. */
  diff: number;
}

/**
 * Standings from finished games only. Ordered by wins, then head-to-head is
 * NOT applied (pools here are small and often incomplete mid-day); diff and
 * points-for break ties, which matches how these tournaments post a running
 * board on the day.
 */
export function computeStandings(teams: Team[], schedule: ScheduledGame[]): StandingRow[] {
  const rows = new Map<string, StandingRow>();
  for (const team of teams) {
    rows.set(team.id, {
      team,
      played: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      pointsFor: 0,
      pointsAgainst: 0,
      diff: 0,
    });
  }

  // Bracket games name their teams by slot, so results only count once the
  // slot resolves to a concrete team. Seeds resolve from pool play alone
  // (`seedsOf` below), which keeps this free of circular dependency.
  const ctx = { teams, schedule, seedsOf: (): string[] => [] };

  for (const game of schedule) {
    if (game.result === undefined) continue;
    const slots = gameSlots(game);
    const teamA = resolveSlot(slots.a, ctx);
    const teamB = resolveSlot(slots.b, ctx);
    if (teamA === null || teamB === null) continue;
    const a = rows.get(teamA.id);
    const b = rows.get(teamB.id);
    if (a === undefined || b === undefined) continue;

    const { scoreA, scoreB } = game.result;
    a.played += 1;
    b.played += 1;
    a.pointsFor += scoreA;
    a.pointsAgainst += scoreB;
    b.pointsFor += scoreB;
    b.pointsAgainst += scoreA;

    if (scoreA > scoreB) {
      a.wins += 1;
      b.losses += 1;
    } else if (scoreB > scoreA) {
      b.wins += 1;
      a.losses += 1;
    } else {
      a.draws += 1;
      b.draws += 1;
    }
  }

  const out = [...rows.values()];
  for (const row of out) row.diff = row.pointsFor - row.pointsAgainst;

  return out.sort(
    (x, y) =>
      // A team with no results sinks below ANY team with one — the sentence
      // `hasResults` has always carried, now enforced where it matters. It
      // used to be only implicit, so a withdrawn team (organiser deletes its
      // games, the team row stays: `deleteTeam` refuses while games reference
      // it) sat on 0-0-0 with diff 0 ABOVE every team that played and lost,
      // and the bracket's seed slots resolved to the withdrawn club.
      (y.played > 0 ? 1 : 0) - (x.played > 0 ? 1 : 0) ||
      y.wins - x.wins ||
      y.diff - x.diff ||
      y.pointsFor - x.pointsFor ||
      x.team.name.localeCompare(y.team.name),
  );
}

/** Teams that have not played yet sink below anyone with a result. */
export function hasResults(rows: StandingRow[]): boolean {
  return rows.some((r) => r.played > 0);
}

/**
 * Standings for one division only. Divisions never share a table: an open
 * team's record must not count games against a women's team, which is exactly
 * what the single flat table used to do on the board AND on the overlay's
 * pregame record line.
 */
export function divisionStandings(
  teams: Team[],
  schedule: ScheduledGame[],
  divisionId: string,
  poolId?: string,
): StandingRow[] {
  const inDivision = groupTeams(teams, divisionId, poolId);
  return computeStandings(inDivision, groupGames(schedule, divisionId, inDivision, poolId));
}

function groupTeams(teams: Team[], divisionId: string, poolId?: string): Team[] {
  return teams.filter(
    (t) => t.divisionId === divisionId && (poolId === undefined || t.poolId === poolId),
  );
}

/**
 * The games that belong to one division (or one pool within it) — the ONE
 * definition, shared by the table and by `seedOrder`'s completeness check.
 * Splitting those two was a real bug: the check used to count a team's
 * appearances across the WHOLE schedule, so one cross-division friendly (or a
 * row with an unresolved side) kept `played < scheduled` forever and froze the
 * division's seeds at "OPEN 1" all weekend with pool play long finished.
 */
function groupGames(
  schedule: ScheduledGame[],
  divisionId: string,
  groupTeamList: Team[],
  poolId?: string,
): ScheduledGame[] {
  const ids = new Set(groupTeamList.map((t) => t.id));
  // Explicit division tag wins; otherwise infer from the teams involved, so
  // games scheduled before divisions existed still land in the right table.
  return schedule.filter((g) => {
    const inDiv = g.divisionId !== undefined ? g.divisionId === divisionId : bothIn(g, ids);
    if (!inDiv) return false;
    // Within a pool, only that pool's games count. A game with no pool tag
    // still counts if both its teams are in this pool (hand-entered rows).
    if (poolId === undefined) return true;
    return g.poolId !== undefined ? g.poolId === poolId : bothIn(g, ids);
  });
}

function bothIn(game: ScheduledGame, ids: Set<string>): boolean {
  const { a, b } = gameSlots(game);
  const side = (s: typeof a): boolean =>
    s !== null && s.kind === "team" && ids.has(s.teamId);
  return side(a) && side(b);
}

/**
 * Seed order for a division: team ids best-first, used to resolve
 * `{ kind: "seed" }` slots. Only POOL games (those with no bracket flag) feed
 * this: otherwise a bracket result would re-seed the bracket that produced it.
 */
export function seedOrder(
  teams: Team[],
  schedule: ScheduledGame[],
  divisionId: string,
  poolId?: string,
): string[] {
  const pool = schedule.filter((g) => g.stage !== "bracket");
  const rows = divisionStandings(teams, pool, divisionId, poolId);

  // A seed is only real once pool play has separated the teams. Before that,
  // computeStandings still returns every team ordered alphabetically, which
  // would render "OPEN 1" as a concrete club and read as though the bracket
  // were already decided. A rank is therefore only claimed when every team in
  // the group has finished all of its pool games.
  //
  // "Its pool games" means THIS GROUP's games — the same filtered set the
  // table above was computed from — and only rows both of whose sides are
  // concrete teams, because those are the only games `played` can ever count.
  // Counting from the whole schedule froze a division's seeds forever the
  // moment any of its teams appeared in a cross-division friendly or a row
  // with an unresolved side.
  const scheduled = new Map<string, number>();
  const group = groupTeams(teams, divisionId, poolId);
  for (const g of groupGames(pool, divisionId, group, poolId)) {
    const slots = gameSlots(g);
    if (slots.a === null || slots.a.kind !== "team") continue;
    if (slots.b === null || slots.b.kind !== "team") continue;
    for (const slot of [slots.a, slots.b]) {
      scheduled.set(slot.teamId, (scheduled.get(slot.teamId) ?? 0) + 1);
    }
  }
  // With no pool games at all, "everyone finished everything" is vacuously
  // true and the alphabetical table would leak out as a decided seeding.
  const totalScheduled = rows.reduce((n, r) => n + (scheduled.get(r.team.id) ?? 0), 0);
  if (totalScheduled === 0) return [];

  const complete = rows.every((r) => r.played >= (scheduled.get(r.team.id) ?? 0));
  if (!complete) return [];

  return rows.map((r) => r.team.id);
}

/** Slot-resolution context wired to real standings. Build once per render. */
export function slotContext(
  teams: Team[],
  schedule: ScheduledGame[],
): {
  teams: Team[];
  schedule: ScheduledGame[];
  seedsOf: (divisionId: string, poolId?: string) => string[];
} {
  const cache = new Map<string, string[]>();
  return {
    teams,
    schedule,
    seedsOf: (divisionId: string, poolId?: string): string[] => {
      const key = `${divisionId}::${poolId ?? ""}`;
      const hit = cache.get(key);
      if (hit !== undefined) return hit;
      const order = seedOrder(teams, schedule, divisionId, poolId);
      cache.set(key, order);
      return order;
    },
  };
}

/** Every division that has at least one team, in display order. */
export function activeDivisions(teams: Team[], divisions: Division[]): Division[] {
  const used = new Set(teams.map((t) => t.divisionId).filter((d): d is string => d !== undefined));
  return divisions.filter((d) => used.has(d.id)).sort((a, b) => a.order - b.order);
}

/**
 * Standings tables to render for a division: one per pool when it has them,
 * otherwise a single flat table. Pools with no teams are skipped so a
 * half-configured draw does not litter the board with empty tables.
 *
 * THE DISPLAYED TABLE COUNTS EVERY STAGE, brackets included — deliberately,
 * and differently from `seedOrder`, which reads pool games only. A spectator's
 * "record" column should say what actually happened this weekend (and a
 * knockout-only event would otherwise show an empty table); seeding must not
 * let a final's result re-seed the bracket that produced it. The two answer
 * different questions, so mid-bracket the table's order can legitimately
 * differ from the order the seed slots resolved from.
 */
export function divisionTables(
  teams: Team[],
  schedule: ScheduledGame[],
  divisionId: string,
  pools: Pool[],
): { key: string; title: string | null; rows: StandingRow[]; progress: Progress }[] {
  const mine = poolsOf(pools, divisionId);
  if (mine.length === 0) {
    const all = groupTeams(teams, divisionId);
    return [
      {
        key: divisionId,
        title: null,
        rows: divisionStandings(teams, schedule, divisionId),
        progress: progressOf(groupGames(schedule, divisionId, all)),
      },
    ];
  }
  const tables = mine.map((p) => ({
    key: p.id,
    title: p.name,
    rows: divisionStandings(teams, schedule, divisionId, p.id),
    progress: progressOf(groupGames(schedule, divisionId, groupTeams(teams, divisionId, p.id), p.id)),
  }));
  // Teams left out of the draw still need somewhere to appear.
  const undrawn = teams.filter((t) => t.divisionId === divisionId && t.poolId === undefined);
  if (undrawn.length > 0) {
    const ids = new Set(undrawn.map((t) => t.id));
    tables.push({
      key: `${divisionId}-undrawn`,
      title: "Unassigned",
      rows: computeStandings(undrawn, schedule),
      // Exactly the games that table counts: both sides left out of the draw.
      progress: progressOf(schedule.filter((g) => bothIn(g, ids))),
    });
  }
  return tables.filter((t) => t.rows.length > 0);
}

/** Pool games played vs scheduled. */
export interface Progress {
  played: number;
  total: number;
}

/**
 * A table's own "played x/y": the games that table is made of, pool stage
 * only. It used to be the division's count on every table, so each of two
 * three-team pools read «played 0/6» over its three games (owner, 2026-09-14).
 */
function progressOf(games: ScheduledGame[]): Progress {
  const pool = games.filter((g) => (g.stage ?? "pool") !== "bracket");
  return { played: pool.filter((g) => g.result !== undefined).length, total: pool.length };
}

/**
 * Whether a division's pool play is over: every scheduled pool game has a
 * result. This is exactly the condition that makes seeds resolvable, so the
 * board uses it to decide when a bracket becomes worth showing: before it, a
 * bracket is a wall of "OPEN 1" placeholders; after it, every name is real.
 */
export function poolPlayComplete(schedule: ScheduledGame[], divisionId: string): boolean {
  const pool = schedule.filter(
    (g) => (g.stage ?? "pool") !== "bracket" && g.divisionId === divisionId,
  );
  return pool.length > 0 && pool.every((g) => g.result !== undefined);
}

/** A whole division's pool games played vs scheduled: how many are left
 *  before its bracket seeds. One table's own line is `divisionTables`'s
 *  `progress`, which a division split into pools does not share. */
export function poolProgress(
  schedule: ScheduledGame[],
  divisionId: string,
): Progress {
  const pool = schedule.filter(
    (g) => (g.stage ?? "pool") !== "bracket" && g.divisionId === divisionId,
  );
  return { played: pool.filter((g) => g.result !== undefined).length, total: pool.length };
}

// ---------- what a pregame game is worth ----------

export interface GroupStake {
  /** The table both teams appear in. `poolId` undefined = the whole division. */
  divisionId: string;
  poolId: string | undefined;
  /** Where each side sits in that table right now, 1-based. */
  rankA: number;
  rankB: number;
  /** Games in the group still unplayed, this one included. */
  remaining: number;
  /**
   * Whoever wins this ends the group with strictly more wins than anyone else
   * can reach. PROVABLE, and deliberately narrow: it only ever claims this when
   * every other game in the group is already played, so the rest of the table
   * is final and no scoreline has to be invented to test it. Anything that
   * would need a tiebreaker to decide is simply not claimed.
   */
  decidesTop: boolean;
}

/**
 * What is at stake in the game about to be played, for the pregame card.
 *
 * `null` unless both sides are real roster teams sharing one table — an ad-hoc
 * scratch game, a cross-division game or a bracket game between teams from
 * different pools has no standing to be at stake, and a card that made one up
 * would be worse than a card that says nothing.
 */
export function groupStake(
  teams: Team[],
  schedule: ScheduledGame[],
  idA: string | null,
  idB: string | null,
  gameId: string | null,
): GroupStake | null {
  if (idA === null || idB === null || idA === idB) return null;
  const a = teams.find((t) => t.id === idA);
  const b = teams.find((t) => t.id === idB);
  if (a === undefined || b === undefined) return null;
  if (a.divisionId === undefined || a.divisionId !== b.divisionId) return null;
  if (a.poolId !== b.poolId) return null;

  const rows = divisionStandings(teams, schedule, a.divisionId, a.poolId);
  const rankA = rows.findIndex((r) => r.team.id === idA) + 1;
  const rankB = rows.findIndex((r) => r.team.id === idB) + 1;
  if (rankA === 0 || rankB === 0) return null;

  // The group's own unplayed games. Pool stage only: a bracket game is not
  // part of the table it feeds.
  const ids = new Set(rows.map((r) => r.team.id));
  const unplayed = schedule.filter((g) => {
    if (g.result !== undefined) return false;
    if ((g.stage ?? "pool") === "bracket") return false;
    const slots = gameSlots(g);
    const sideIds = [slots.a, slots.b].map((sl) =>
      sl !== null && sl.kind === "team" ? sl.teamId : null,
    );
    return sideIds.every((id) => id !== null && ids.has(id));
  });

  const rest = rows.filter((r) => r.team.id !== idA && r.team.id !== idB);
  const bestOther = rest.reduce((m, r) => Math.max(m, r.wins), 0);
  const worstOfTheTwo = Math.min(
    rows[rankA - 1]?.wins ?? 0,
    rows[rankB - 1]?.wins ?? 0,
  );

  return {
    divisionId: a.divisionId,
    poolId: a.poolId,
    rankA,
    rankB,
    remaining: unplayed.length,
    decidesTop:
      unplayed.length === 1 &&
      gameId !== null &&
      unplayed[0]?.id === gameId &&
      worstOfTheTwo + 1 > bestOther,
  };
}
